import {WebVitalsService} from '@app/shared/services/web-vitals.service';
import {environment} from "@env";
import {LngRoutePipe} from "@app/shared/pipes";
import {ViewportScroller} from "@angular/common";
import {NzSafeAny} from "ng-zorro-antd/core/types";
import {NzMessageService} from "ng-zorro-antd/message";
import {filter, finalize, first} from "rxjs/operators";
import {LngKeys, LocalStorageKeys} from "@app/shared/enums";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {CookieService} from "@app/shared/services/cookie.service";
import {NzModalModule, NzModalService} from "ng-zorro-antd/modal";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {AppStateService} from "@app/shared/services/app-state.service";
import {getItemStore, setItemStore} from "@app/shared/helpers/storage";
import {AnalyticsService} from "@app/shared/services/analytics.service";
import {HelperComponent} from "@app/shared/ui-kits/helper/helper.component";
import {AuthService, PaymentService, TemplateService} from "@app/shared/services";
import {NzNotificationComponent, NzNotificationService} from "ng-zorro-antd/notification";
import {ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet} from "@angular/router";
import {parseJwt, parseQueryParams, redirectToTranslatedPath, removeQueryParams} from "@app/shared/helpers";
import {
  ARABIC_SPEAKING_COUNTRIES,
  CHINESE_SPEAKING_COUNTRIES,
  FRENCH_SPEAKING_COUNTRIES,
  GERMAN_SPEAKING_COUNTRIES,
  LANGUAGES_OBJ,
  lngTxtObj,
  route_paths,
  PORTUGUESE_SPEAKING_COUNTRIES,
  SPANISH_SPEAKING_COUNTRIES
} from "@app/shared/constants/languages_data";
import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  DOCUMENT,
  inject,
  OnInit,
  Renderer2,
  TemplateRef,
  viewChild,
  ViewChild
} from '@angular/core';
import {
  AmeriaBankQueryParams,
  AmeriaPaymentConfirmRes,
  JWT,
  MySocialUser,
  Template,
  User
} from "@app/shared/interfaces";
import {
  StudentsPremiumBannerComponent
} from "@app/shared/ui-kits/students-premium-banner/students-premium-banner.component";

// declare const Paddle: any;
declare let gtag: Function;
declare const google: any;

@Component({
  selector: 'sf-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterLink,
    RouterOutlet,
    LngRoutePipe,
    TranslatePipe,
    NzModalModule,
    HelperComponent,
    StudentsPremiumBannerComponent
  ]
})
export class AppComponent implements OnInit {
  protected readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  protected readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly cookieService = inject(CookieService);
  private readonly webVitals = inject(WebVitalsService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly paymentService = inject(PaymentService);
  // private readonly paymentService = inject(PaymentService);
  private readonly nzModalService = inject(NzModalService);
  private readonly templateService = inject(TemplateService);
  protected readonly appStateService = inject(AppStateService);
  private readonly translateService = inject(TranslateService);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  protected readonly nzNotificationService = inject(NzNotificationService);

  private readonly cookieTmRef = viewChild.required<TemplateRef<{
    $implicit: NzNotificationComponent;
    data: NzSafeAny;
  }>>('CookieTmRef');

  private readonly premiumStudentRef = viewChild.required<TemplateRef<{
    $implicit: NzNotificationComponent;
    data: NzSafeAny;
  }>>('PremiumStudentRef');

  @ViewChild('NofBtnRef', {static: true})
  private readonly nofBtnRef!: TemplateRef<{ $implicit: NzNotificationComponent }>;

  constructor() {
    afterNextRender(() => {
      this.cookieService.init();
      this.webVitals.start();
      this.appStateService.init_auth_token();
      this.getMe();
      this.listenToInternetConnection();
      this.getUserLocation();
      this.appStateService.getAllConstants();
      setTimeout(() => this.initializeGoogleSignIn(), 5000);
      setTimeout(() => this.confirmAmeriaBankPayment());

      setTimeout(() => this.changeDetectorRef.detectChanges());
      this.cookieService.showCookieBanner(this.cookieTmRef());
    });
  }

  ngOnInit() {
    this.listenToUserLogin();
    this.listenToRouterEvents();

    this.getLngFromRoute();
    this.onLangChange();

    this.appStateService.logOut$
      .subscribe(() => {
        setTimeout(() => this.initializeGoogleSignIn(), 5000);
      });

    // this.loadPHIcons();
  }

  private showPremiumStudentBanner() {
    setTimeout(() => {
      const state = getItemStore<boolean | null>(LocalStorageKeys.studentsFreeBannerInfoSeen)
      const user = this.appStateService.user;

      if (!state && user?.hasPromotion && user?.emailVerified) {
        this.nzNotificationService.template(this.premiumStudentRef(), {
          nzDuration: 50 * 1000,
          nzPlacement: 'bottomLeft',
          nzClass: 'sf-cookie-notification',
          nzStyle: {borderRadius: '8px'},
        })
          .onClose
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            setItemStore<boolean>(LocalStorageKeys.studentsFreeBannerInfoSeen, true);
          });
      }
    }, 2000);
  }

  private loadPHIcons() {
    ['bold', 'fill'].forEach((weight) => {
      const link = this.document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://cdn.jsdelivr.net/npm/@phosphor-icons/web@2.1.1/src/${weight}/style.min.css`;
      this.document.head.appendChild(link);
    });
  }

  private getLngFromRoute() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        first()
      )
      .subscribe(() => {
        const lng = this.currentLngKey;

        this.appStateService.setLng(lng);
      });
  }

  private onLangChange() {
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.appStateService.dateTime = new Date().getTime();

        Object.values(LngKeys).forEach((key) => {
          this.renderer.removeClass(this.document.body, `lang_${key}`);
        });

        this.renderer.addClass(this.document.body, `lang_${res.lang}`);

        this.appStateService.setHtmlLangAttribute(res.lang, this.renderer);
        this.appStateService.addMetaTags(LANGUAGES_OBJ[res.lang as LngKeys].seo);

        setTimeout(() => this.initializeGoogleSignIn(), 5000);
      });
  }

  private get currentLngKey() {
    const cleanUrl = this.router.url.split('?')[0]; // Remove query params
    const paths = cleanUrl.split('/').filter((path) => path.length);

    let lng: LngKeys = paths.at(0) as LngKeys || LngKeys.EN;
    if (!Object.values(LngKeys).includes(lng as LngKeys)) {
      lng = LngKeys.EN;
    }

    return lng;
  }

  private getUserLocation() {
    this.paymentService.getUserLocation()
      .pipe(
        finalize(() => this.appStateService.isUserPlansReady = true),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        this.appStateService.userLocation = res;
        this.appStateService.userLocationInit$.next(res);
        this.analyticsService.track('User location', res);

        setTimeout(() => {
          // const countryLng = 'at' as any;
          const countryLng = res.country.toLowerCase();
          const currentLng = this.translateService.currentLang;

          const onTheSameLng = countryLng === currentLng;
          const isCountryLngExist = Object.values(LngKeys).includes(countryLng as LngKeys);

          if (onTheSameLng) {
            return;
          }

          const frenchKeyCodes = FRENCH_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());
          const germanKeyCodes = GERMAN_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());
          const spanishKeyCodes = SPANISH_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());
          const portugueseKeyCodes = PORTUGUESE_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());
          const arabicKeyCodes = ARABIC_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());
          const chineseKeyCodes = CHINESE_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());

          if (frenchKeyCodes.includes(countryLng)) {
            this.showChangeLngNof(lngTxtObj.fr);
            return;
          }

          if (germanKeyCodes.includes(countryLng)) {
            this.showChangeLngNof(lngTxtObj.de);
            return;
          }

          if (spanishKeyCodes.includes(countryLng)) {
            this.showChangeLngNof(lngTxtObj.es);
            return;
          }

          if (portugueseKeyCodes.includes(countryLng)) {
            this.showChangeLngNof(lngTxtObj.pt);
            return;
          }

          if (arabicKeyCodes.includes(countryLng)) {
            this.showChangeLngNof(lngTxtObj.ar);
            return;
          }

          if (chineseKeyCodes.includes(countryLng)) {
            this.showChangeLngNof(lngTxtObj.zh);
            return;
          }

          if (!isCountryLngExist) {
            if (currentLng !== LngKeys.EN) {
              this.showChangeLngNof(lngTxtObj.en);
            }
            return;
          }

          if (lngTxtObj[countryLng as keyof typeof lngTxtObj]) {
            this.showChangeLngNof(lngTxtObj[countryLng as keyof typeof lngTxtObj]);
          }
        }, 1000);
      });
  }

  private showChangeLngNof(data: { txt: string; lng: LngKeys }) {
    const urls = [
      this.appStateService.getLngRout('documents'),
      'template-print',
      'confirm-email',
      'ats-checker',
      'resume-examples',
      'career-blog'
    ];

    if (urls.some((url) => this.router.url.includes(url))) {
      return;
    }

    const lng = this.appStateService.languages.find(lng => lng.key === data.lng);
    let src = `/assets/img/languages/${data.lng}.svg`;

    if (lng) {
      src = this.getLngImgSrc(lng.name) || src;
    }

    const title = `<div class="d-flex ai-c gap-12"><img class="w-24" src="${src}" alt="selfcv">${data.txt}</div>`;

    this.nzNotificationService.blank(
      title,
      '',
      {
        nzButton: this.nofBtnRef,
        nzPlacement: 'bottomLeft',
        nzDuration: 15 * 1000,
        nzPauseOnHover: true,
        nzAnimate: true,
        nzClass: 'sf-lng-switch-nof'
      }
    );
  }

  private getLngImgSrc(myStr: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(myStr, 'text/html');

    const img = doc.querySelector('img');
    const src = img?.getAttribute('src');

    return src;
  }

  protected changeLanguage() {
    // let key = 'at' as keyof SEO_Configs;
    let key = this.appStateService.userLocation?.country?.toLowerCase() as LngKeys;

    const frenchKeyCodes = FRENCH_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());
    const germanKeyCodes = GERMAN_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());
    const spanishKeyCodes = SPANISH_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());
    const portugueseKeyCodes = PORTUGUESE_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());
    const arabicKeyCodes = ARABIC_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());
    const chineseKeyCodes = CHINESE_SPEAKING_COUNTRIES.map(item => item.keyCode.toLowerCase());

    if (frenchKeyCodes.includes(key)) {
      key = LngKeys.FR;
    }

    if (germanKeyCodes.includes(key)) {
      key = LngKeys.DE;
    }

    if (spanishKeyCodes.includes(key)) {
      key = LngKeys.ES;
    }

    if (portugueseKeyCodes.includes(key)) {
      key = LngKeys.PT;
    }

    if (arabicKeyCodes.includes(key)) {
      key = LngKeys.AR;
    }

    if (chineseKeyCodes.includes(key)) {
      key = LngKeys.ZH;
    }

    if (!Object.values(LngKeys).includes(key)) {
      key = LngKeys.EN;
    }

    this.appStateService.setLng(key);
    this.appStateService.setHtmlLangAttribute(key, this.renderer);
    this.appStateService.addMetaTags(LANGUAGES_OBJ[key].seo);

    redirectToTranslatedPath(this.router, key, this.appStateService);
  }

  private confirmAmeriaBankPayment() {
    if (this.appStateService.isAuthenticated) {
      const queryParams: AmeriaBankQueryParams = parseQueryParams(window.location.search || '');

      if (queryParams) {

        const condition = Object.hasOwn(queryParams, 'paymentID') &&
          ['resposneCode', 'responseCode'].some((item) => Object.hasOwn(queryParams, item));

        if (condition) {
          this.paymentService.confirmAmeriaPayment(queryParams.paymentID)
            .pipe(
              finalize(() => removeQueryParams(this.router)),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((res: AmeriaPaymentConfirmRes) => {
              this.nzMessageService.create(res.status.toLowerCase(), res.description);
              this.appStateService.user!.planIsValid = res.planIsValid;
              this.appStateService.user!.activeUntil = res.activeUntil;
              this.analyticsService.track("Payment Confirmed!!!", res);
              this.getMe();
            });
        }
      }

    }
  }

  private listenToInternetConnection() {
    window.addEventListener('online', () => {
      this.nzMessageService.success(
        this.translateService.instant('project_messages.you_online')
      );
    });

    window.addEventListener('offline', () => {
      this.nzMessageService.warning(
        this.translateService.instant('project_messages.you_offline')
      );
    });
  }

  private listenToUserLogin() {
    this.appStateService.user$
      .pipe(
        filter((user) => Boolean(user))
      )
      .subscribe(() => {
        this.getUserTemplates();
      });
  }

  private listenToRouterEvents() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        const url = (event as NavigationEnd).urlAfterRedirects.split(/[?#]/, 1)[0];
        const privatePaths = Object.values(route_paths).flatMap(paths => [
          paths.auth, paths.sign_in, paths.sign_up, paths.forgot_password, paths.reset_password,
          paths.account, paths.user, paths.security, paths.billing_history, paths.choose_plan,
          paths.dashboard, paths.documents
        ]).map(path => path.replace(/\/+$/, ''));
        const privateRoute = privatePaths.some(path => url === path || url.startsWith(`${path}/`)) ||
          /\/(?:template-print|partner-premium|confirm-email)(?:\/|$)|\/career-blog\/(?:create|edit)(?:\/|$)/.test(url);
        if (privateRoute) {
          this.appStateService.setNoIndexPageSeo();
        }

        if (this.appStateService.isBrowser) {
          this.scrollToTop();

          if (environment.env === 'production') {
            this.trackEvent(event as NavigationEnd);
          }
        }

        this.nzModalService.closeAll();
      });
  }

  private scrollToTop() {
    this.viewportScroller.scrollToPosition([0, 0]);
    this.document.body.scrollTop = 0;

    setTimeout(() => {
      window.scrollTo(0, 0);
      this.document.body.scrollTo(0, 0);
    }, 100);

    window.scrollTo(0, 0);
    this.document.body.scrollTo(0, 0);
  }

  private trackEvent(event: NavigationEnd) {
    gtag('set', 'page_path', event.urlAfterRedirects);
    gtag('event', 'page_view');
  }

  private getMe() {
    if (this.appStateService.authToken || this.appStateService.onPrintPage()) {
      this.authService.getMe()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res: User) => {
          this.cookieService.acceptCookies();
          this.appStateService.user = res;
          this.appStateService.user$.next(res);
          this.analyticsService.identifyMixpanelUser(res);
          this.getUserTemplates();

          this.showPremiumStudentBanner();

          console.log('user', res);
        });
    }
  }

  private getUserTemplates() {
    this.templateService.getTemplates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: Template[]) => {
        this.appStateService.userTemplates = res;
        this.appStateService.userTemplates$.next(res);
        this.changeDetectorRef.detectChanges();
      });
  }

  private async initializeGoogleSignIn() {
    const condition = this.appStateService.isBrowser &&
      !this.appStateService.isAuthenticated;

    if (condition) {
      const condition = ![
        this.appStateService.getLngRout('sign_in'),
        this.appStateService.getLngRout('sign_up')
      ]
        .some((route) => this.router.url.includes(route))

      if (condition) {
        await this.cookieService.loadGoogleSignIn();
        google.accounts.id.initialize({
          client_id: environment.google_oauth_client_id,
          callback: this.googleSocialSignIn.bind(this)
        });

        google.accounts.id.prompt();
      }
    }
  }

  private googleSocialSignIn(data: { credential: string; select_by: string; }) {
    const user: MySocialUser = {
      credential: data.credential,
      provider: 'google'
    };

    this.authService.socialSignIn(user)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: JWT) => {
          // Adding userId so we can track
          const jwtContent = parseJwt(res.accessToken);
          user.id = jwtContent.userId;

          this.getMe();
          this.router.navigateByUrl(this.appStateService.returnUrl(this.activatedRoute));
        },
        error: () => {
          this.nzMessageService.error(
            this.translateService.instant('project_messages.social_login_failed')
          );
        }
      });
  }

  /*private getTemplatesToSelect() {
    this.templateService.getTemplatesToSelect()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: Template[]) => {
        // this.appStateService.templates = res;
        console.log(res);
      });
  }*/

  /*private getAPIAddress() {
    this.paymentService.getAPIAddress()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        console.log('APIAddress', res);

        /!*interval(500)
          .pipe(
            takeWhile(() => !Paddle, true),
            filter(() => Boolean(Paddle))
          )
          .subscribe(() => {
            this.setUpJSPaddle(res);
          });*!/

      });
  }*/

  /*private getPaymentPrices() {
    this.paymentService.getPaymentPrices()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        console.log('billing prices', res);
        this.appStateService.billingPlans = res.response.products;
      });
  }*/

  /*private setUpJSPaddle(res: string) {
    Paddle.Environment.set("sandbox");

    Paddle.Initialize({
      token: "test_382d3434e3c21706bd450a58a0b",
      eventCallback: (data: any) => {
        console.log("------------data-------------", data);

        if (data.name === 'checkout.completed') {
          this.appStateService.checkoutComplete$.next();
          setTimeout(() => this.getMe(), 3000);
        }


        if (data.name === PaddleEventTypes.LOADED) {
          Paddle.Spinner.hide();
          // set here some variable showCheckoutInfo: true
          // set user email and pass to that form
        }

        if (data.name === PaddleEventTypes.CUSTOMER_CREATED) {
          // Update price based on the price coming with event (total, tax ... see consoled)
          console.log('PaddleEventTypes.CUSTOMER_CREATED', data);
          console.log(data.data);
          console.log(data.data.currency_code);
          console.log(data.data.totals);
        }

        if (data.name === PaddleEventTypes.CUSTOMER_UPDATED) {
          // when user changes the country - this will update the price accordingly
          // because we have some country with different tax and also our custom country prices

          console.log('PaddleEventTypes.CUSTOMER_UPDATED', data);
          console.log(data.data);
          console.log(data.data.currency_code);
          console.log(data.data.totals);
        }

        if (data.name === PaddleEventTypes.CHECKOUT_COUPON_REMOVE) {
          // this must be handled, related coupon
        }

        if (data.name === PaddleEventTypes.PAYMENT_COMPLETE) {
          // Checkout payment completed event
          console.log('PaddleEventTypes.PAYMENT_COMPLETE');
        }

        if (data.name === PaddleEventTypes.COMPLETE) {
          // here we can close checkout form and navigate user to dashboard
          console.log('PaddleEventTypes.COMPLETE', data.eventData); // Data specifics on the event
          this.appStateService.checkoutComplete$.next();
          this.getMe();
        } else if (data.name === PaddleEventTypes.CLOSE) {
          console.log(data.eventData); // Data specifics on the event
        } else if (data.name === PaddleEventTypes.ERROR) {
          // generic errors
          console.log(data.eventData); // Data specifics on the event
        }
      }
    });

    const request: PricePreviewBody = {
      items: pricePreviewBodyItems,
      customerIpAddress: res
    };

    Paddle.PricePreview(request)
      .then((pricePreview: PricePreview) => {
        this.appStateService.pricePreview = pricePreview;
        console.log('pricePreview', pricePreview);
      })
      .catch((error: any) => {
        /!* TODO Armen cover this *!/
        // If not supported country will be passed, there could be errors.
        // It is important to pass supported country only in the paddle payment list.
        // If there is an error, we can pass some default country - for example Germany code (DE).
        console.error('Paddle.PricePreview error', error);
      });
  }*/

  protected onConfirmCookie(notification: NzNotificationComponent) {
    notification.close();
    this.cookieService.acceptCookies();
  }

}
